import {
  colid,
  dupsTableName,
  logsTableName,
  noShowsLogsTableName,
  noShowTableName,
  prompts,
  tableName,
} from "../components/env";
import JSONToFormattedText from "../components/JSONToMarkdown";
import NoShows from "../components/NoShows";
import { getSupabase, getSupabaseRole } from "../components/supabaseClient";
import { isReadOnlyRole } from "../config/roles";
import { isImpersonating } from "./impersonation";
import { convertScheduledTime } from "./scheduleTimeDisplay";

// Observability log writes are silently skipped during view-as so admin's
// browsing doesn't pollute the target's audit logs and so the frontend
// write-block Proxy doesn't throw and break the calling flow.
export const logEvent = async (logLevel, message, metadata = {}) => {
  if (isImpersonating()) return;
  const supabase = await getSupabase();
  // Read-only roles (e.g. patients) can't write audit logs — the write-block
  // Proxy would throw and break the caller — so skip, like the view-as guard.
  if (isReadOnlyRole(getSupabaseRole())) return;
  const { error } = await supabase
    .from(logsTableName)
    .insert([{ log_level: logLevel, message, metadata }]);

  if (error) {
    console.error("Error logging event:", error);
  }
};
export const logDupEvent = async (logLevel, message, metadata = {}) => {
  if (isImpersonating()) return;
  const supabase = await getSupabase();
  if (isReadOnlyRole(getSupabaseRole())) return;
  const { error } = await supabase
    .from(dupsTableName)
    .insert([{ log_level: logLevel, message, metadata }]);

  if (error) {
    console.error("Error logging event:", error);
  }
};
export const logNoShowEvent = async (logLevel, message, metadata = {}) => {
  if (isImpersonating()) return;
  const supabase = await getSupabase();
  if (isReadOnlyRole(getSupabaseRole())) return;
  const { error } = await supabase
    .from(noShowsLogsTableName)
    .insert([{ log_level: logLevel, message, metadata }]);

  if (error) {
    console.error("Error logging event:", error);
  }
};
//helper to convert date
export function convertDate(dateStr) {
  const [year, month, day] = dateStr.split("-");

  const monthInt = parseInt(month);
  const dayInt = parseInt(day);

  // Return the date in M/D/YYYY format
  return `${monthInt}/${dayInt}/${year}`;
}

// Convert a time string to minutes since midnight for chronological sorting.
// Handles the formats seen across schedules:
//   "HH:MM:SS+00"  — PostgREST `time with time zone` (e.g. "09:00:00+00")
//   "HH:MM:SS"     — bare 24-hour (e.g. "9:00:00", "14:30:00")
//   "HH:MM"        — short form (e.g. "09:00", "9:00")
//   "HH:MM-HH:MM"  — range form (e.g. "09:00-09:30") — uses the start time
//   "H:MM AM/PM"   — 12-hour with suffix (e.g. "9:00 AM", "1:30 PM")
// Unparseable values sort last (Infinity). A numeric compare is required because
// localeCompare on raw strings MISORDERS non-zero-padded hours ("9:00" sorts
// after "10:00" since "9" > "1") and breaks entirely for AM/PM strings.
export function timeToMinutes(rawTime) {
  if (!rawTime) return Infinity;
  // Strip trailing tz offset ("+00", "+00:00", "Z", "-07:00") then a range end-time.
  let t = String(rawTime).trim().replace(/[+Z].*$/, "").replace(/-\d{2}(:\d{2})?$/, "");
  const dashIdx = t.indexOf("-");
  if (dashIdx > 0) t = t.slice(0, dashIdx); // non-padded range like "9:00-9:30"
  t = t.trim();
  const ampm = t.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)$/i);
  if (ampm) {
    let h = parseInt(ampm[1], 10);
    const min = parseInt(ampm[2], 10);
    const meridiem = ampm[3].toUpperCase();
    if (meridiem === "AM" && h === 12) h = 0;
    if (meridiem === "PM" && h !== 12) h += 12;
    return h * 60 + min;
  }
  const parts = t.split(":");
  const h = parseInt(parts[0], 10);
  const m = parseInt(parts[1] || "0", 10);
  if (Number.isNaN(h) || Number.isNaN(m)) return Infinity;
  return h * 60 + m;
}

//helper sort time
export function sortObjectsByTime(objects, timeKey, options = {}) {
  // Chronological sort by appointment time (earliest → latest). In-place +
  // returns, matching the previous contract. See timeToMinutes for the formats.
  const {
    dateKey = null,
    sourceTimezoneKey = "timezone_source",
    viewerTimezone = null,
  } = options || {};
  const comparableTime = (row) => {
    if (dateKey && viewerTimezone) {
      return convertScheduledTime(
        row?.[dateKey],
        row?.[timeKey],
        row?.[sourceTimezoneKey],
        viewerTimezone,
      );
    }
    return row?.[timeKey];
  };
  return objects.sort((a, b) => timeToMinutes(comparableTime(a)) - timeToMinutes(comparableTime(b)));
}

export function sortObjectsByDateTime(objects, dateKey, timeKey) {
  // First validate and filter out invalid entries
  const validObjects = objects.filter(obj => 
    obj && obj[dateKey] && typeof obj[dateKey] === 'string' && 
    obj[timeKey] && typeof obj[timeKey] === 'string'
  );

  // Sort the array of objects first by date, then by time
  return validObjects.sort((a, b) => {
    try {
      // Compare dates first
      const dateCompare = b[dateKey].localeCompare(a[dateKey]);
      if (dateCompare !== 0) return dateCompare;

      // If dates are equal, compare times
      const timeA = a[timeKey].split("-")[0] || a[timeKey];
      const timeB = b[timeKey].split("-")[0] || b[timeKey];
      return timeB.localeCompare(timeA);
    } catch (error) {
      console.error('Error sorting:', error, 'Objects:', { a, b });
      return 0;
    }
  });
}

export const handleViewAudioFiles = (jsonContent) => {
  try {
    const jsonData = JSON.parse(jsonContent);
    const formattedText = JSONToFormattedText(jsonData);
    openHTMLInNewTab(formattedText);
  } catch (error) {
    console.error("Error parsing JSON content:", error);
    alert("Error parsing JSON content");
  }
};

export const openHTMLInNewTab = (htmlContent) => {
  const newWindow = window.open("", "_blank");
  newWindow.document.write(`
    <!DOCTYPE html>
    <html lang="en">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>HTML Viewer</title>
      </head>
      <body>
        <div id="content">${htmlContent}</div>
      </body>
    </html>
  `);
  newWindow.document.close();
};

export async function copyItems(noShowsID, dupsID = undefined, metadataFn = undefined) {
  const supabase = await getSupabase();
  // Read-only roles (patients) never run this maintenance copy — its writes are
  // blocked by the read-only Proxy and would throw.
  if (isReadOnlyRole(getSupabaseRole())) return;
  // user_id is required for the no_show INSERT to pass RLS (policy:
  // auth.uid()::text = user_id). Stamp every inserted row with the current
  // session's user id.
  const { data: sessionData } = await supabase.auth.getSession();
  const currentUserId = sessionData?.session?.user?.id || null;
  // Get columns from prompts array and add required system columns
  const commonColumns = [
    "id",
    "date_",
    "time_",
    ...prompts.map((prompt) => prompt[0]),
  ];

  // Fetch items with only the common columns
  const { data: items, error: fetchError } = await supabase
    .from(tableName)
    .select(commonColumns.join(","))
    .in("id", noShowsID);

  if (fetchError) {
    console.error("Error fetching items:", fetchError);
    return { success: false, error: fetchError };
  }

  if (items.length === 0) {
    console.log("No items found with the specified IDs.");
    return { success: false, error: "No items found" };
  }

  // Insert filtered items into no-show table — optionally attach per-row
  // no_show_metadata (reason + factors) when the caller supplies metadataFn.
  // no_show has no phone columns (tel_num, home_phone), so both are dropped.
  const filteredItems = items.map(({ submission_url, tel_num, home_phone, type, ...rest }) => {
    const base = currentUserId ? { ...rest, user_id: currentUserId } : rest;
    if (typeof metadataFn === "function") {
      return { ...base, no_show_metadata: metadataFn(rest) };
    }
    return base;
  });

  // Insert filtered items into no-show table
  const { error: insertError } = await supabase
    .from(noShowTableName)
    .insert(filteredItems);

  if (insertError) {
    console.error("Error inserting items into no-show table:", insertError);
    return { success: false, error: insertError };
  }
  await logNoShowEvent("INFO", `Moved scheduleIDs: ${noShowsID}`, [
    { date: items[0].date_, ids: noShowsID },
  ]);

  if (dupsID) {
    const { error: deleteError } = await supabase
      .from(tableName)
      .delete()
      .in("id", dupsID);

    if (deleteError) {
      console.error("Error deleting items from original table:", deleteError);
      return { success: false, error: deleteError };
    }
    console.log(
      "Items successfully moved to no-shows table and deleted from original table."
    );
  }

  return { success: true };
}
//Showed up
export async function deleteItems(ShowsID) {
  const supabase = await getSupabase();
  const { error: fetchError } = await supabase
    .from(noShowTableName)
    // .select("*")
    .delete() // Adjust fields to fetch specific columns ///// can improve eff if use curr date
    .in("id", ShowsID);

  if (fetchError) {
    console.error("Error fetching items:", fetchError);
    return;
  }
  await logNoShowEvent("INFO", `Removed scheduleIDs: ${ShowsID}`, [
    { date: getFormattedDateWithOffset(0), ids: ShowsID },
  ]);
}
function getFormattedDateWithOffset(dayOffset) {
  const today = new Date();
  // Adjust the date by the offset
  today.setDate(today.getDate() - dayOffset);

  // Extract the year, month, and day
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0"); // Months are 0-indexed
  const day = String(today.getDate()).padStart(2, "0");

  // Return in the desired format
  return `${year}-${month}-${day}`;
}
export function dupByCol(objects, timeKey) {
  const latestEmails = new Map();

  for (const obj of objects) {
    latestEmails.set(obj[colid], obj);
  }

  return Array.from(latestEmails.values())
    .sort((a, b) => new Date(a[timeKey]) - new Date(b[timeKey]))
    .map((obj) => obj["id"]);
}
export async function deleteItemsByDate(day) {
  const supabase = await getSupabase();
  // Patients / read-only roles don't run the daily no-show cleanup — its deletes
  // and audit-log writes are blocked by the read-only Proxy and would throw.
  if (isReadOnlyRole(getSupabaseRole())) return;
  console.log("\n🚀 Starting deleteItemsByDate");
  
  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - day);
  
  console.log("📅 Date Range Check:");
  console.log(`   Start: ${startDate.toISOString().split('T')[0]}`);
  console.log(`   End: ${endDate.toISOString().split('T')[0]}`);

  const summary = {
    dates_checked: 0,
    dates_with_records: 0,
    total_records_found: 0,
    previously_processed: 0,
    newly_processed: 0,
    duplicates_removed: 0
  };
  const globalLogMetadata = [];

  for (let i = 0; i < day; i++) {
    const d = getFormattedDateWithOffset(i);
    summary.dates_checked++;
    
    // First, get ALL no-shows for this date
    let { data: allNoShows } = await supabase
      .from(noShowTableName)
      .select("*")
      .eq("date_", d);

    // Then, get only unprocessed ones
    let { data: unprocessedNoShows } = await supabase
      .from(noShowTableName)
      .select("*")
      .eq("date_", d)
      .eq("processed", false);

    if (allNoShows?.length > 0) {
      summary.dates_with_records++;
      summary.total_records_found += allNoShows.length;
      summary.previously_processed += allNoShows.filter(item => item.processed).length;

      // Process unprocessed records
      if (unprocessedNoShows?.length > 0) {
        const pstTime = new Date().toLocaleString("en-US", { timeZone: "America/Los_Angeles" });
        
        // First mark as processed in no-shows table
        const { error: updateError } = await supabase
          .from(noShowTableName)
          .upsert(
            unprocessedNoShows.map(item => ({
              ...item,
              processed: true,
              processed_at: pstTime
            })),
            { onConflict: ["id"] }
          );

        if (!updateError) {
          // Then delete from main schedules table
          const { error: deleteError } = await supabase
            .from(tableName)
            .delete()
            .in('id', unprocessedNoShows.map(item => item.id));

          if (deleteError) {
            console.error(`❌ Error deleting from schedules table:`, deleteError);
          } else {
            console.log(`✅ Deleted ${unprocessedNoShows.length} records from schedules table`);
            summary.newly_processed += unprocessedNoShows.length;
            
            // Add to detailed actions
            globalLogMetadata.push({
              date: d,
              records_status: {
                total_records: allNoShows.length,
                newly_processed: {
                  count: unprocessedNoShows.length,
                  ids: unprocessedNoShows.map(item => ({
                    id: item.id,
                    processed_at: pstTime
                  }))
                },
                previously_processed: {
                  count: allNoShows.filter(item => item.processed).length,
                  ids: allNoShows.filter(item => item.processed).map(item => ({
                    id: item.id,
                    processed_at: item.processed_at
                  }))
                }
              }
            });
          }
        }
      }
    }
  }

  const finalLog = {
    summary: {
      date_range: {
        start: startDate.toISOString().split('T')[0],
        end: endDate.toISOString().split('T')[0],
        total_days: day
      },
      processing_results: {
        dates_checked: summary.dates_checked,
        dates_with_records: summary.dates_with_records,
        record_counts: {
          total: summary.total_records_found,
          previously_processed: summary.previously_processed,
          newly_processed: summary.newly_processed,
          unprocessed_remaining: 0
        }
      },
      status: `Processed ${summary.newly_processed} new records. ${summary.previously_processed} were already processed.`
    },
    detailed_actions: globalLogMetadata
  };

  await logEvent("INFO", `Daily Cleanup Run ${new Date().toISOString()}`, finalLog);
  console.log("🏁 Finished deleteItemsByDate function");
}

//fetch one sche
export async function fetchSche(scheId) {
  const supabase = await getSupabase();
  const { data, error } = await supabase
    .from(tableName)
    .select("*")
    .eq("id", scheId)
    .single();
  if (error) {
    console.error("Error fetching more audios", error);
    return;
  }
  return data;
}
export async function fetchNoShows(selectedDate) {
  const supabase = await getSupabase();
  const { data, error } = await supabase
    .from(noShowTableName)
    .select("*")
    .eq("date_", selectedDate);
  if (error) {
    console.error("Error fetching schedules:", error.message || error);
  }
  return data;
}

// Live Transcription localStorage utilities
export const getLiveTranscriptionSetting = (patientId) => {
  // First check if there's a global setting
  const globalSetting = localStorage.getItem('live_transcription_global');
  
  // If global setting exists, use it (unless patient has an explicit override)
  if (globalSetting !== null) {
    const globalValue = globalSetting === 'true';
    
    // Check if this patient has an explicit override
    const patientOverride = localStorage.getItem(`live_transcription_${patientId}`);
    if (patientOverride !== null) {
      // Patient has explicit setting, use it
      const result = patientOverride === 'true';
      return result;
    } else {
      // No patient override, use global setting
      return globalValue;
    }
  }
  
  // No global setting, check patient-specific setting
  const localStorageKey = `live_transcription_${patientId}`;
  const value = localStorage.getItem(localStorageKey);
  const result = value === null ? false : value === 'true'; // Default to false if not set

  return result;
};

export const setLiveTranscriptionSetting = (patientId, enabled) => {
  const localStorageKey = `live_transcription_${patientId}`;
  localStorage.setItem(localStorageKey, enabled.toString());
};

// Global live transcription setting functions
export const getGlobalLiveTranscriptionSetting = () => {
  const value = localStorage.getItem('live_transcription_global');
  return value === null ? null : value === 'true';
};

export const setGlobalLiveTranscriptionSetting = (enabled) => {
  localStorage.setItem('live_transcription_global', enabled.toString());
};

export const clearGlobalLiveTranscriptionSetting = () => {
  localStorage.removeItem('live_transcription_global');
};

export const clearAllPatientLiveTranscriptionOverrides = () => {
  // Get all localStorage keys that start with live_transcription_
  const keysToRemove = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith('live_transcription_') && key !== 'live_transcription_global') {
      keysToRemove.push(key);
    }
    if (key && key.startsWith('live_fact_extraction_') && key !== 'live_fact_extraction_global') {
      keysToRemove.push(key);
    }
  }
  
  // Remove all patient-specific overrides
  keysToRemove.forEach(key => {
    localStorage.removeItem(key);
  });
  
  console.log(`🌍 Cleared ${keysToRemove.length} patient-specific live transcription overrides`);
};

export const clearLiveTranscriptionSetting = (patientId) => {
  const localStorageKey = `live_transcription_${patientId}`;
  localStorage.removeItem(localStorageKey);
};

// Live Fact Extraction localStorage utilities
export const getLiveFactExtractionSetting = (patientId) => {
  const localStorageKey = `live_fact_extraction_${patientId}`;
  const value = localStorage.getItem(localStorageKey);
  console.log(`🔍 getLiveFactExtractionSetting for patient ${patientId}:`, {
    localStorageKey,
    value,
    valueType: typeof value,
    result: value === null ? true : value === 'true'
  });
  return value === null ? true : value === 'true'; // Default to true if not set
};

export const setLiveFactExtractionSetting = (patientId, enabled) => {
  const localStorageKey = `live_fact_extraction_${patientId}`;
  localStorage.setItem(localStorageKey, enabled.toString());
};

// Live Medical Codes localStorage utilities
export const getLiveMedicalCodesSetting = (patientId) => {
  // First check if there's a global setting
  const globalSetting = localStorage.getItem('live_medical_codes_global');
  
  // If global setting exists, use it (unless patient has an explicit override)
  if (globalSetting !== null) {
    const globalValue = globalSetting === 'true';
    
    // Check if this patient has an explicit override
    const patientOverride = localStorage.getItem(`live_medical_codes_${patientId}`);
    if (patientOverride !== null) {
      // Patient has explicit setting, use it
      const result = patientOverride === 'true';
      console.log(`🏥 getLiveMedicalCodesSetting for patient ${patientId}:`, {
        globalSetting: globalValue,
        patientOverride: result,
        result: result
      });
      return result;
    } else {
      // No patient override, use global setting
      console.log(`🏥 getLiveMedicalCodesSetting for patient ${patientId}:`, {
        globalSetting: globalValue,
        patientOverride: 'none',
        result: globalValue
      });
      return globalValue;
    }
  }
  
  // No global setting, check patient-specific setting
  const localStorageKey = `live_medical_codes_${patientId}`;
  const value = localStorage.getItem(localStorageKey);
  const result = value === null ? false : value === 'true'; // Default to false if not set
  
  // Only log if there's a value or if it's different from default
  if (value !== null || result === false) {
    console.log(`🏥 getLiveMedicalCodesSetting for patient ${patientId}:`, {
      localStorageKey,
      value,
      valueType: typeof value,
      result
    });
  }
  
  return result;
};

export const setLiveMedicalCodesSetting = (patientId, enabled) => {
  const localStorageKey = `live_medical_codes_${patientId}`;
  localStorage.setItem(localStorageKey, enabled.toString());
};

export const clearLiveFactExtractionSetting = (patientId) => {
  const localStorageKey = `live_fact_extraction_${patientId}`;
  localStorage.removeItem(localStorageKey);
};

// Auto-save utility functions
export const getAutoSaveSetting = (patientId) => {
  if (!patientId) return true; // Default to true if no patient ID
  const setting = localStorage.getItem(`auto_save_${patientId}`);
  return setting === null ? null : setting === 'true'; // Return null if not set, boolean if set
};

export const setAutoSaveSetting = (patientId, enabled) => {
  if (!patientId || enabled === undefined || enabled === null) {
    console.warn('setAutoSaveSetting called with invalid parameters:', { patientId, enabled });
    return;
  }
  localStorage.setItem(`auto_save_${patientId}`, enabled.toString());
};

export const clearAutoSaveSetting = (patientId) => {
  if (!patientId) return;
  localStorage.removeItem(`auto_save_${patientId}`);
};
